import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Bell, BellOff, CheckCheck, Pin } from 'lucide-react';
import type { NotificationDto } from '@nexops/shared';
import { useI18n } from '@/i18n/I18nProvider';
import { cn } from '@/shared/lib/cn';
import { formatRelative } from '@/shared/lib/format';
import { Badge, Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, EmptyState, Skeleton, Tooltip } from '@/shared/ui';
import { useQuery } from '@tanstack/react-query';
import { notifKeys, notificationsApi } from './api/notifications.api';
import { CategoryTile } from './components/CategoryTile';
import { useNotificationActions } from './hooks/useNotificationActions';
import { useNotificationSummary } from './hooks/useNotificationSummary';
import { isInternalLink } from './lib/group';

/** Topbar quick dropdown: unread badge (from /summary), latest 15 (pinned first), mark all read, link to the centre. */
export function NotificationsBell() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const summary = useNotificationSummary();
  const query = useQuery({
    queryKey: notifKeys.bell,
    queryFn: () => notificationsApi.list({}, 1, 15),
    enabled: open,
    staleTime: 0,
    meta: { silent: true },
  });
  const { read, readAll } = useNotificationActions();

  const items = query.data?.items ?? [];
  const unread = summary.data?.unread ?? query.data?.unread ?? 0;

  const openItem = (n: NotificationDto) => {
    if (!n.read && !(n.requiresConfirm && !n.confirmedAt)) read.mutate(n.id);
    if (!n.link) return navigate('/notifications');
    if (isInternalLink(n.link)) navigate(n.link);
    else window.open(n.link, '_blank', 'noopener,noreferrer');
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <Tooltip content={t('topbar.notifications')} side="bottom">
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative" aria-label={unread > 0 ? `${t('topbar.notifications')} (${unread})` : t('topbar.notifications')}>
            <Bell />
            {unread > 0 && (
              <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-background tabular">
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
      </Tooltip>
      <DropdownMenuContent className="w-[23rem] max-w-[calc(100vw-1.5rem)] p-0">
        <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2.5">
          <div>
            <div className="whitespace-nowrap text-[13px] font-semibold">{t('notif.title')}</div>
            {unread > 0 && <div className="text-xs text-muted-foreground">{t('notif.unread', { count: unread })}</div>}
          </div>
          {unread > 0 && (
            <Button variant="ghost" size="sm" loading={readAll.isPending} onClick={() => readAll.mutate(undefined)}>
              {!readAll.isPending && <CheckCheck />}
              {t('notif.markAll')}
            </Button>
          )}
        </div>

        <div className="max-h-[26rem] overflow-y-auto p-1">
          {query.isLoading ? (
            <div className="space-y-1 p-1">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-14" />
              ))}
            </div>
          ) : query.isError ? (
            <EmptyState compact icon={<AlertTriangle />} title={t('notif.loadError')} action={<Button size="sm" onClick={() => void query.refetch()}>{t('common.retry')}</Button>} />
          ) : items.length === 0 ? (
            <EmptyState compact icon={<BellOff />} title={t('notif.empty')} description={t('notif.emptyBody')} />
          ) : (
            items.map((n) => (
              <DropdownMenuItem key={n.id} onSelect={() => openItem(n)} className="items-start gap-2.5 py-2">
                <CategoryTile category={n.category} size="sm" dim={n.read} className="mt-0.5" />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className={cn('block min-w-0 truncate text-[13px] leading-snug', n.read ? 'text-foreground/80' : 'font-semibold')}>{n.title}</span>
                    {n.priority === 'HIGH' && <Badge tone="danger">{t('notifs.priority.urgent')}</Badge>}
                    {n.pinned && <Pin className="!size-3 shrink-0 !text-warning" aria-label={t('notifs.item.pinned')} />}
                  </span>
                  {n.body && <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">{n.body}</span>}
                  <span className="mt-1 block text-2xs text-muted-foreground">{formatRelative(n.createdAt, locale)}</span>
                </span>
                {!n.read && <span className="mt-2 size-2 shrink-0 rounded-full bg-primary" aria-hidden />}
              </DropdownMenuItem>
            ))
          )}
        </div>

        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/notifications')} className="justify-center py-2.5 font-semibold text-primary">
          {t('notifs.bell.viewAll')}
          <ArrowRight className="!text-primary" />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
